import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import {
    Reactions,
    Thread,
    ThreadDialogService,
    ThreadInputAreaComponent,
    ThreadMessageComponent,
    ThreadMessageUnderModeration,
    Threads,
    ThreadService,
    TimelineItem
} from "@teammood/timeline";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { catchError, EMPTY, filter, Subject, switchMap, takeUntil, tap } from "rxjs";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { LoggerService } from "@teammood/shared/util/logger";
import { UserInfos } from "../../shared/ui/username-icon/user-infos.type";
import { ThreadReactionsComponent } from "./thread-reactions/thread-reactions.component";

@Component({
    selector: "tm-threads",
    templateUrl: "./threads.component.html",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateModule,
        ThreadInputAreaComponent,
        ThreadMessageUnderModeration,
        ThreadMessageComponent,
        ThreadReactionsComponent
    ]
})
export class ThreadsComponent implements OnInit, OnDestroy {

    @Input() timelineItem: TimelineItem & Threads & Reactions;

    @Input() discuss: boolean;

    @Input() userInfos: UserInfos;

    @Output()
    onThreadsUpdated: EventEmitter<TimelineItem & Threads> = new EventEmitter();

    @ViewChild("newThreadComponent")
    threadInputAreaComponent: ThreadInputAreaComponent;

    protected threads: Thread[];

    protected newThread: Thread;

    protected editMode: boolean = false;

    protected threadsLimitTo: number = -2;

    private _destroy$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private translate: TranslateService,
        private threadService: ThreadService,
        private snackBarService: SnackBarService,
        private threadDialogService: ThreadDialogService,
    ) {}

    ngOnInit() {
        this.threads = this.timelineItem.threads;
        this.newThread = new Thread();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    protected showMore(){
        this.threadsLimitTo = this.threadsLimitTo - 10;
    }

    protected async deleteThread(thread: Thread){
        const dialogRef = await this.threadDialogService.delete();

        dialogRef.beforeClosed().pipe(
            filter(shouldDelete => !!shouldDelete),
            switchMap((_) => {
                return this.threadService.deleteThread(this.timelineItem.id, this.timelineItem.itemType, thread.id).pipe(
                    tap(updatedTimelineItem => {
                        this.snackBarService.success(this.translate.instant('thread_deleted'))
                        this.threads = updatedTimelineItem.threads;
                        this.onThreadsUpdated.emit(updatedTimelineItem);
                    })
                );
            }),
            catchError(error => {
                this.logger.error('Error deleting thread:', error);
                this.snackBarService.error('Oops')
                return EMPTY;
            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    protected async moderateThread(thread: Thread){
        const dialogRef = await this.threadDialogService.moderate();

        dialogRef.beforeClosed().pipe(
            filter(why => !!why),
            switchMap((why) => {
                return this.threadService.moderateThread(this.timelineItem.id, this.timelineItem.itemType, thread.id, why).pipe(
                    tap(updatedTimelineItem => {
                        this.snackBarService.success(this.translate.instant('moderation_success'))
                        this.threads = updatedTimelineItem.threads;
                        this.onThreadsUpdated.emit(updatedTimelineItem);
                    })
                );
            }),
            catchError(error => {
                this.logger.error('Error moderating thread:', error);
                this.snackBarService.error('Oops')
                return EMPTY;
            }),
            takeUntil(this._destroy$)
          ).subscribe();
    }

    protected  onEditedThread(thread: Thread) {
        this.threadService.updateThread(this.timelineItem.id, this.timelineItem.itemType, thread).pipe(
            tap(updatedTimelineItem => {
                this.snackBarService.success(this.translate.instant('thread_updated'));
                this.threads = updatedTimelineItem.threads;
                this.onThreadsUpdated.emit(updatedTimelineItem);
            }),
            catchError(error => {
                this.logger.error('Error updating thread:', error);
                this.snackBarService.error('Oops')
                return EMPTY;
            }),
        ).subscribe();
    }

    protected onThreadAdded(newThread: Thread){
        if (!newThread.message.isEmpty()) {
            this.threadService.addThread(this.timelineItem.id, this.timelineItem.itemType, newThread.message, newThread.isAnonymous).pipe(
                tap(updatedTimelineItem => {
                    this.threads = updatedTimelineItem.threads;
                    this.onThreadsUpdated.emit(updatedTimelineItem);
                }),
                catchError(error => {
                    this.logger.error('Error adding thread:', error);
                    this.snackBarService.error('Oops')
                    return EMPTY;
                })
            ).subscribe();
        }
    }

    onThreadReactionsUpdated(updatedTimelineItem){ 
        this.threads = updatedTimelineItem.threads;
        this.onThreadsUpdated.emit(updatedTimelineItem);
    }

    protected onCancel() {
        this.discuss = false;
    }

    startDiscussion(){
        this.discuss = true;
    }

    showMentions() {
        this.discuss = true;
        setTimeout(() => {
            this.threadInputAreaComponent.showMentions();
        }, 500);
    }
}